import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TenantDomain } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Badge, Button, Card, ConfirmButton, EmptyState, PageHeader, Spinner, TextField } from '../../components/ui';

const List = z.array(TenantDomain);
const KEY = ['domains'] as const;
const TONE = { verified: 'good', pending_dns: 'warn', failed: 'bad', disabled: 'neutral' } as const;

function DnsRecord({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-slate-600">{label}</dt>
      <dd className="break-all font-mono text-sm text-slate-900">{value}</dd>
    </div>
  );
}

function DomainRow({ d }: { d: TenantDomain }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const check = useMutation({ mutationFn: () => api.post(`/domains/${d.id}/check`, { schema: TenantDomain }), onSuccess: refresh });
  const makePrimary = useMutation({ mutationFn: () => api.patch(`/domains/${d.id}`, { body: { primary: true }, schema: TenantDomain }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api.delete(`/domains/${d.id}`), onSuccess: refresh });
  const error = check.error ?? makePrimary.error ?? remove.error;
  const waiting = d.status === 'pending_dns' || d.status === 'failed';
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="break-all text-base font-semibold text-slate-900">{d.hostname}</h3>
        <Badge tone={TONE[d.status]}>{t(`setup.domains.status.${d.status}`)}</Badge>
        {d.primary ? <Badge tone="good">{t('setup.domains.primary')}</Badge> : null}
        <span className="text-sm text-slate-600">{t(`setup.domains.kind.${d.kind}`)}</span>
      </div>
      <p className="mt-1 text-sm text-slate-600">
        {d.verifiedAt ? t('setup.domains.verifiedAt', { time: formatDateTime(d.verifiedAt) }) : d.lastCheckedAt ? t('setup.domains.checkedAt', { time: formatDateTime(d.lastCheckedAt) }) : t('setup.domains.notChecked')}
      </p>
      {d.errorKey ? (
        <p className="mt-1 text-sm text-red-700" role="status">
          {t(d.errorKey)}
        </p>
      ) : null}
      {waiting && d.kind === 'custom' ? (
        <div className="mt-3 rounded-md bg-slate-50 p-3">
          <p className="mb-2 text-sm text-slate-700">{t('setup.domains.dnsHelp')}</p>
          <dl className="grid gap-3 sm:grid-cols-2">
            <DnsRecord label={t('setup.domains.txtName')} value={d.verifyRecordName} />
            <DnsRecord label={t('setup.domains.txtValue')} value={d.verifyRecordValue} />
            <DnsRecord label={t('setup.domains.cname')} value={d.cnameTarget} />
          </dl>
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <ErrorLine error={error} />
        {waiting ? (
          <Button disabled={check.isPending} onClick={() => check.mutate()}>
            {t('setup.domains.checkNow')}
          </Button>
        ) : null}
        {d.status === 'verified' && !d.primary ? (
          <Button disabled={makePrimary.isPending} onClick={() => makePrimary.mutate()}>
            {t('setup.domains.makePrimary')}
          </Button>
        ) : null}
        <ConfirmButton label={t('setup.domains.remove')} confirmLabel={t('setup.domains.removeConfirm')} pending={remove.isPending} onConfirm={() => remove.mutate()} />
      </div>
    </li>
  );
}

function AddForm() {
  const qc = useQueryClient();
  const [hostname, setHostname] = useState('');
  const add = useMutation({
    mutationFn: () => api.post('/domains', { body: { hostname }, schema: TenantDomain }),
    onSuccess: () => {
      setHostname('');
      return qc.invalidateQueries({ queryKey: KEY });
    },
  });
  const errs = fieldErrors(add.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  return (
    <Card title={t('setup.domains.addTitle')} description={t('setup.domains.addHelp')} className="mb-6">
      <form onSubmit={submit} className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
        <div className="sm:col-span-2">
          <ErrorLine error={add.error && Object.keys(errs).length === 0 ? add.error : null} />
        </div>
        <TextField label={t('setup.domains.hostname')} hint={t('setup.domains.hostnameHint')} required maxLength={253} value={hostname} onChange={(e) => setHostname(e.target.value)} error={errs['hostname']} autoComplete="off" />
        <Button type="submit" variant="primary" disabled={add.isPending || hostname.trim() === ''}>
          {t('setup.domains.add')}
        </Button>
      </form>
    </Card>
  );
}

/** Custom domains (core-phase4 s5): add a hostname, copy the DNS records, check now, choose the primary one, remove. */
export function DomainsPage() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api.get('/domains', { schema: List }) });
  return (
    <>
      <PageHeader title={t('setup.domains.title')} description={t('setup.domains.description')} />
      <AddForm />
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : q.data.length === 0 ? (
        <EmptyState message={t('setup.domains.empty')} />
      ) : (
        <ul className="space-y-4" aria-label={t('setup.domains.title')}>
          {q.data.map((d) => (
            <DomainRow key={d.id} d={d} />
          ))}
        </ul>
      )}
    </>
  );
}
